import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import accounting from 'accounting';
import SessionStore from 'stores/SessionStore';
import {
  getAccessorials,
  getStandardAccessorials,
  upsertStandardAccessorials,
} from 'utils/AccessorialWebAPIUtils';
import ToastStore from 'stores/ToastStore';
import TextField from 'components/v2/forms/TextField';
import CargomaticSelect from 'components/v2/select';
import LegTypeBadge from 'components/v2/LegTypeBadge';
import { LEG_TYPES } from 'constants/AccessorialRates';
import { titleize } from 'underscore.string';
import { MoreHorizontal, Plus } from 'lucide-react';
import {
  BadgeAlt as Badge,
  Button,
  DialogFooter,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@cargomatic/ui';

export default function StandardAccessorialRates({
  canUpdate,
  type,
  markets,
  onHide,
}) {
  const [standardAccessorialRates, setStandardAccessorialRates] = useState([]);
  const [accessorials, setAccessorials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [isValid, setIsValid] = useState(true);

  const { t } = useTranslation();

  useEffect(() => {
    fetchAccessorials();
    fetchStandardAccessorialRates();
  }, []);

  const fetchAccessorials = async () => {
    try {
      const result = await getAccessorials({ paginate: false });
      const {
        entity: { items },
      } = result;

      const list = items.map((a) => ({
        name: a.name,
        code: a.charge_code,
      }));

      setAccessorials(list);
    } catch (_e) {
      ToastStore.addNotification({
        title: t('LOADING_ACCESSORIALS_ERROR'),
        body: '',
        level: 'error',
      });
    }
  };

  const fetchStandardAccessorialRates = async () => {
    try {
      const filterString = { isActive: true, paginate: false };

      if (markets) {
        filterString.market = markets;
        filterString.customerType = type;
      }
      const query = new URLSearchParams(filterString).toString();
      const result = await getStandardAccessorials(query);
      const {
        entity: {
          results: { standardAccessorialRates },
        },
      } = result;
      if (standardAccessorialRates && standardAccessorialRates.length) {
        setStandardAccessorialRates(
          standardAccessorialRates.map((r) => ({
            ...r,
            _localId: r._localId || uuidv4(),
          })),
        );
      }
      setLoading(false);
      setIsButtonDisabled(false);
    } catch (_e) {
      ToastStore.addNotification({
        title: titleize(t('ERROR')),
        body: t('FAILED_TO_LOAD_ACCESSORIALS'),
        level: 'error',
      });
    }
  };

  const handleValueChange = (value, field, index) => {
    setIsValid(true);
    const newAccessorialRates = [...standardAccessorialRates];
    const targetStdAccessorialRate = {
      ...standardAccessorialRates[index],
      [field]: value.name || value,
    };
    newAccessorialRates[index] = targetStdAccessorialRate;
    setStandardAccessorialRates(newAccessorialRates);
  };

  const handleAccessorialChange = (value, index) => {
    setIsValid(true);
    const { name } = value;
    const acc = accessorials.find((a) => a.name === name);
    const targetAccessorial = {
      ...standardAccessorialRates[index],
      accessorialType: name,
      accessorialCode: acc.code || '',
    };
    const newItems = [...standardAccessorialRates];
    newItems[index] = targetAccessorial;

    setStandardAccessorialRates(newItems);
  };

  const handleEditButtonClick = (item, index) => {
    const targetStdAccessorialRate = {
      ...item,
      editing: !item.editing,
      update: true,
    };
    const newItems = [...standardAccessorialRates];
    newItems[index] = targetStdAccessorialRate;
    setStandardAccessorialRates(newItems);
  };

  const removeStandardAccessorialRateRow = (index) => {
    setStandardAccessorialRates((prev) => {
      return [...prev.slice(0, index), ...prev.slice(index + 1)];
    });
  };

  const handleDeleteButtonClick = async (standardAccessorialRate, index) => {
    setIsValid(true);
    const standardAccessorialRateToDelete = { ...standardAccessorialRate };
    if (
      standardAccessorialRateToDelete.createdAt ||
      standardAccessorialRateToDelete.id
    ) {
      standardAccessorialRateToDelete.isActive = false;
      setIsButtonDisabled(true);
      const result = await handleUpsertStandardAccessorialRates([
        standardAccessorialRateToDelete,
      ]);
      setIsButtonDisabled(false);
      if (!result) {
        return;
      }
    }
    removeStandardAccessorialRateRow(index);
  };

  function addRow() {
    const newItem = {
      _localId: uuidv4(),
      accessorialType: '',
      accessorialCode: '',
      rateType: '',
      rate: 0,
      unit: '',
      market: markets[0],
      legType: '',
      editing: true,
      update: true,
      isActive: true,
    };
    const newItems = [...standardAccessorialRates, newItem];
    setStandardAccessorialRates(newItems);
  }

  const handleSave = async () => {
    const valid = validate();

    if (!valid) {
      return setIsValid(false);
    }
    setIsValid(true);

    const accessorialRates = [];
    standardAccessorialRates.forEach((item) => {
      if (item.update) {
        const entry = {
          accessorialType: item.accessorialType,
          accessorialCode: item.accessorialCode,
          rateType: item.rateType,
          rate: item.rate,
          unit: item.unit,
          customerType: type,
          market: item.market,
          legType: item.legType || null,
          isActive: item.isActive,
        };

        if (item.id) {
          entry.id = item.id;
        }

        accessorialRates.push(entry);
      }
    });
    setLoading(true);
    setIsButtonDisabled(true);
    const result = accessorialRates.length
      ? await handleUpsertStandardAccessorialRates(accessorialRates)
      : true;
    setLoading(false);
    setIsButtonDisabled(false);
    if (result) {
      onHide();
    }
  };

  async function handleUpsertStandardAccessorialRates(accessorialRates) {
    try {
      const result = await upsertStandardAccessorials(accessorialRates);
      if (result) {
        const {
          entity: {
            results: {
              result: { total },
            },
          },
        } = result;
        if (total && total.flatten().length) {
          ToastStore.addNotification({
            title: t('STANDARD_ACCESSORIAL_RATES'),
            body: t('SUCCESSFULLY_UPDATED'),
            level: 'success',
          });
        }
      }
      return result;
    } catch {
      ToastStore.addNotification({
        title: t('STANDARD_ACCESSORIAL_RATES'),
        body: 'Unable to update or delete standard accessorial rate',
        level: 'error',
      });
      return false;
    }
  }

  function validate() {
    if (!standardAccessorialRates || !standardAccessorialRates.length) {
      return false;
    }
    for (let i = 0; i < standardAccessorialRates.length; i++) {
      const item = standardAccessorialRates[i];
      if (
        !(
          item.accessorialType &&
          item.accessorialCode &&
          item.rateType &&
          item.rate >= 0 &&
          item.unit &&
          item.market
        )
      ) {
        return false;
      }
    }
    return true;
  }

  const renderTypeBadge = () => {
    if (type === 'carrier') {
      return (
        <Badge
          variant="outline"
          className="bg-blue-100 text-blue-800 border-blue-300"
        >
          {titleize(t('CARRIER'))}
        </Badge>
      );
    }
    return (
      <Badge
        variant="outline"
        className="bg-green-100 text-green-800 border-green-300"
      >
        {titleize(t('SHIPPER'))}
      </Badge>
    );
  };

  const renderActionsMenu = (item, i) => {
    if (!canUpdate) return null;
    const canEdit = SessionStore.isAbleToEditAccessorialLaneRate();
    return (
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="w-8 h-8 p-0"
            disabled={!canEdit || isButtonDisabled}
          >
            <span className="sr-only">Actions</span>
            <MoreHorizontal className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => handleEditButtonClick(item, i)}>
            Edit
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleDeleteButtonClick(item, i)}>
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  function renderReadOnlyRow(item, i) {
    return (
      <TableRow key={item.id || item._localId}>
        <TableCell>{renderTypeBadge()}</TableCell>
        <TableCell>{item.market}</TableCell>
        <TableCell>{item.accessorialType}</TableCell>
        <TableCell>{item.accessorialCode}</TableCell>
        <TableCell>{item.rateType}</TableCell>
        <TableCell>{accounting.formatMoney(item.rate, '$', 0)}</TableCell>
        <TableCell>{item.unit}</TableCell>
        <TableCell>
          <LegTypeBadge legType={item.legType} />
        </TableCell>
        <TableCell className="text-right">
          {renderActionsMenu(item, i)}
        </TableCell>
      </TableRow>
    );
  }

  function renderEditableRow(item, i) {
    return (
      <TableRow key={item.id || item._localId}>
        <TableCell>{renderTypeBadge()}</TableCell>
        <TableCell>
          <CargomaticSelect
            dataTestId="CargomaticSelect-market"
            onSelect={(value) => handleValueChange(value, 'market', i)}
            title={t('MY_FILTERS')}
            items={markets}
            selected={item.market}
          />
        </TableCell>
        <TableCell>
          {accessorials.length > 0 && (
            <CargomaticSelect
              dataTestId="CargomaticSelect-accessorial"
              onSelect={(value) => handleAccessorialChange(value, i)}
              title={t('MY_FILTERS')}
              caption={t('SELECT_ACCESSORIALS')}
              showCaption
              items={accessorials}
              selected={item.accessorialType}
              textKey="name"
              valueKey="name"
            />
          )}
        </TableCell>
        <TableCell>{item.accessorialCode}</TableCell>
        <TableCell>
          <CargomaticSelect
            dataTestId="CargomaticSelect-rateType"
            onSelect={(value) => handleValueChange(value, 'rateType', i)}
            title={t('MY_FILTERS')}
            items={['$', '%']}
            selected={item.rateType}
          />
        </TableCell>
        <TableCell>
          <div className="w-20">
            <TextField
              dataTestId="TextField-rate"
              type="number"
              value={item.rate}
              placeholder={t('ENTER_RATE')}
              onChange={(value) => handleValueChange(value, 'rate', i)}
            />
          </div>
        </TableCell>
        <TableCell>
          <CargomaticSelect
            dataTestId="CargomaticSelect-unit"
            items={[
              { name: 'per day', localizationSlug: 'PER_DAY' },
              { name: 'per event', localizationSlug: 'PER_EVENT' },
            ]}
            onSelect={(value) => handleValueChange(value, 'unit', i)}
            caption={t('UNIT', { count: 1 })}
            selected={item.unit}
            textKey="localizationSlug"
            valueKey="name"
          />
        </TableCell>
        <TableCell>
          <CargomaticSelect
            dataTestId="CargomaticSelect-legType"
            items={LEG_TYPES.map((lt) => ({ name: lt.value, label: lt.label }))}
            onSelect={(value) => handleValueChange(value, 'legType', i)}
            caption={t('LEG_TYPE')}
            selected={item.legType}
            textKey="label"
            valueKey="name"
          />
        </TableCell>
        <TableCell className="text-right">
          {renderActionsMenu(item, i)}
        </TableCell>
      </TableRow>
    );
  }

  const COLUMN_COUNT = 9;

  return (
    <div>
      {canUpdate && (
        <div className="flex justify-end mb-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={addRow}
            aria-label={t('ADD_STANDARD_ACCESSORIAL_RATE')}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      )}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Customer Type</TableHead>
            <TableHead>Market</TableHead>
            <TableHead>Accessorial Type</TableHead>
            <TableHead>Accessorial Code</TableHead>
            <TableHead>$/% </TableHead>
            <TableHead>Rate</TableHead>
            <TableHead>Unit</TableHead>
            <TableHead>Leg Type</TableHead>
            <TableHead className="text-right" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <TableRow key={i}>
                {Array.from({ length: COLUMN_COUNT }).map((_, colIdx) => (
                  <TableCell key={colIdx}>
                    <Skeleton className="h-4 w-full" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : standardAccessorialRates && standardAccessorialRates.length ? (
            standardAccessorialRates.map((item, i) =>
              item.editing
                ? renderEditableRow(item, i)
                : renderReadOnlyRow(item, i),
            )
          ) : (
            <TableRow>
              <TableCell colSpan={COLUMN_COUNT} className="text-center py-12">
                {t('EMPTY_STATE')}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {!isValid && (
        <div className="flex justify-center">
          <span className="text-red-500 mt-4">
            {t('PLEASE_CHECK_REQUIRED_DATA')}
          </span>
        </div>
      )}
      <DialogFooter className="mt-6">
        <Button variant="outline" onClick={onHide} disabled={isButtonDisabled}>
          Cancel
        </Button>
        {canUpdate && (
          <Button onClick={handleSave} disabled={isButtonDisabled}>
            Save
          </Button>
        )}
      </DialogFooter>
    </div>
  );
}
